import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, toDisplayString as _toDisplayString, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "mt-1" }
const _hoisted_2 = { class: "mt-4" }
const _hoisted_3 = { class: "mt-4" }
const _hoisted_4 = { class: "mt-4" }

import { ref } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import {
  CodesCreateDocument,
  KitCreateInput,
  LicenseCreateInput,
  CouponCreateInput,
  CodeType,
  OrganizationType,
} from '@/gql/graphql';
import {
  BAlert,
  BCard,
  BButton,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
} from 'bootstrap-vue-next';
import OrganizationPicker from '@/components/organization/OrganizationPicker.vue';

// --- Component emits ---

export default /*@__PURE__*/_defineComponent({
  __name: 'CodeGenerator',
  emits: ['codes-generated'],
  setup(__props, { emit: __emit }) {

const emits = __emit;

// --- New Codes Generated ---
const newCodes = ref<CodeType[]>([]);

function newCodesGenerated(items: (CodeType | null)[]) {
  newCodes.value = items
    .filter((code): code is CodeType => code !== null)
    .map((code) => ({
      ...code,
      code: code.code,
    }));
  showNewCodesModal.value = true;
}

const showNewCodesModal = ref(false);

// --- Code Generator Parameters ---
const codeLength = ref(4);
const count = ref(1);
const organization = ref<OrganizationType | null>(null);
const orderRef = ref('');
const comment = ref('');

const kitName = ref('CD45+ PBMCs');
const kitDescription = ref('Kit for smart encapsulation of PBMCs (CD45+).');
const kitVersion = ref('');
const kitBarcode = ref('BC1M');
const kitUmiLength = ref(8);
const kitDetails = ref({});
const kitSampleCount = ref(10);
const kitExpirationPeriod = ref(12);
const kitManufacturedDate = ref('');
const generateKit = ref(true);
const generateLicense = ref(false);
const generateCouponStorage = ref(true);
const generateCouponCompute = ref(true);

// --- Mutations State ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

// --- Mutation: Create Codes ---
const {
  mutate: executeCreateCodesMutation,
  onDone: onCreateCodesMutationDone,
  onError: onCreateCodesMutationError,
} = useMutation(CodesCreateDocument);

onCreateCodesMutationDone((res) => {
  mutationInProgress.value = false;
  if (
    res.data &&
    res.data.codesCreate &&
    res.data.codesCreate !== null &&
    res.data.codesCreate.length > 0
  ) {
    console.debug(res.data.codesCreate.length, 'codes generated');
    newCodesGenerated(res.data.codesCreate as (CodeType | null)[]);
    mutationShowSuccess.value = true;
    emits('codes-generated');
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'Codes generation failed: ' + res.data;
    console.error('Codes generation failed:', res.data);
  }
});

onCreateCodesMutationError((err) => {
  console.error('Error generating codes:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function onCodesCreateRequest() {
  console.debug('Codes generation requested');

  // define kit
  let kitCreateInput: KitCreateInput | null = null;

  if (generateKit.value) {
    kitCreateInput = {
      name: kitName.value,
      description: kitDescription.value,
      version: kitVersion.value,
      barcode: kitBarcode.value,
      umiLength: Number(kitUmiLength.value),
      details: kitDetails.value,
      sampleCount: Number(kitSampleCount.value),
      expirationMonths: Number(kitExpirationPeriod.value),
      manufacturedAt: kitManufacturedDate.value
        ? new Date(kitManufacturedDate.value)
        : null,
    };
  }

  // define license
  const licenseCreateInput: LicenseCreateInput | null = null;

  // define coupons
  let couponCreateInputs: CouponCreateInput[] = [];

  // define coupons - storage
  if (generateCouponCompute.value)
    couponCreateInputs.push({
      couponType: 'storage',
      units: 'samples',
      unitsTotal: Number(kitSampleCount.value),
      details: kitDetails.value,
      expiresAt: null,
    });

  // define coupons - compute
  if (generateCouponStorage.value)
    couponCreateInputs.push({
      couponType: 'compute',
      units: 'samples',
      unitsTotal: Number(kitSampleCount.value),
      details: kitDetails.value,
      expiresAt: null,
    });

  // execute mutation
  mutationInProgress.value = true;
  mutationError.value = null;
  executeCreateCodesMutation({
    codeLength: Number(codeLength.value),
    count: Number(count.value),
    organizationId: organization.value?.id || null,
    orderRef: orderRef.value,
    comment: comment.value,
    kit: kitCreateInput,
    license: licenseCreateInput,
    coupons: couponCreateInputs,
  });
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BCard), null, {
      header: _withCtx(() => [...(_cache[19] || (_cache[19] = [
        _createElementVNode("strong", null, "Generate new codes with associated kits, licenses and coupons", -1)
      ]))]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _createVNode(_unref(BForm), {
            onSubmit: _withModifiers(onCodesCreateRequest, ["prevent"]),
            size: "sm"
          }, {
            default: _withCtx(() => [
              _createElementVNode("h5", _hoisted_1, [
                _createVNode(_component_font_awesome_icon, {
                  icon: "barcode",
                  class: "me-2"
                }),
                _cache[20] || (_cache[20] = _createTextVNode(" Basic code configuration ", -1))
              ]),
              _createVNode(_unref(BFormGroup), {
                label: "Code length",
                "label-for": "code-length",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormInput), {
                    id: "code-length",
                    name: "codeLength",
                    modelValue: codeLength.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((codeLength).value = $event)),
                    type: "number",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), {
                label: "Number of codes to generate",
                "label-for": "count",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormInput), {
                    id: "count",
                    name: "count",
                    modelValue: count.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((count).value = $event)),
                    type: "number",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), {
                label: "Order reference",
                "label-for": "order-ref",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormInput), {
                    id: "order-ref",
                    name: "orderRef",
                    modelValue: orderRef.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((orderRef).value = $event)),
                    placeholder: "SH-2025-1284",
                    type: "text"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), {
                label: "Comment",
                "label-for": "comment",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormTextarea), {
                    id: "comment",
                    name: "comment",
                    modelValue: comment.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((comment).value = $event)),
                    type: "text"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createElementVNode("h5", _hoisted_2, [
                _createVNode(_component_font_awesome_icon, {
                  icon: "building",
                  class: "me-2"
                }),
                _cache[21] || (_cache[21] = _createTextVNode(" Association with an organization ", -1))
              ]),
              _createVNode(OrganizationPicker, {
                class: "mt-3",
                modelValue: organization.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((organization).value = $event)),
                label: "Organization",
                allowNullOrganization: true,
                includeOwnerFullName: true,
                includeOwnerEmail: true,
                includeCountryCode: true
              }, null, 8, ["modelValue"]),
              _createElementVNode("h5", _hoisted_3, [
                _createVNode(_component_font_awesome_icon, {
                  icon: "cube",
                  class: "me-2"
                }),
                _cache[22] || (_cache[22] = _createTextVNode(" Kit ", -1))
              ]),
              _createVNode(_unref(BFormCheckbox), {
                id: "generate-kit",
                name: "generate-kit",
                modelValue: generateKit.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((generateKit).value = $event)),
                class: "mt-3"
              }, {
                default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                  _createTextVNode(" Create kit ", -1)
                ]))]),
                _: 1
              }, 8, ["modelValue"]),
              _createVNode(_unref(BFormGroup), {
                label: "Kit name",
                "label-for": "kit-name",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormInput), {
                    id: "kit-name",
                    name: "kitName",
                    modelValue: kitName.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((kitName).value = $event)),
                    placeholder: "CD45+ PMBCs",
                    type: "text",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), {
                label: "Kit description",
                "label-for": "kit-description",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormTextarea), {
                    id: "kit-description",
                    name: "kitDescription",
                    modelValue: kitDescription.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((kitDescription).value = $event)),
                    type: "text"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), {
                label: "Kit version",
                "label-for": "kit-version",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormInput), {
                    id: "kit-version",
                    name: "kitVersion",
                    modelValue: kitVersion.value,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((kitVersion).value = $event)),
                    placeholder: "",
                    type: "text"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), {
                label: "Number of samples",
                "label-for": "kit-sample-count",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormInput), {
                    id: "kit-sample-count",
                    name: "kitSampleCount",
                    modelValue: kitSampleCount.value,
                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((kitSampleCount).value = $event)),
                    type: "number",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), {
                label: "Barcodes whitelist",
                "label-for": "kit-barcode",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormInput), {
                    id: "kit-barcode",
                    name: "kitBarcode",
                    modelValue: kitBarcode.value,
                    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((kitBarcode).value = $event)),
                    type: "text",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), {
                label: "UMI length",
                "label-for": "kit-umi-length",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormInput), {
                    id: "kit-umi-length",
                    name: "kitUmiLength",
                    modelValue: kitUmiLength.value,
                    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((kitUmiLength).value = $event)),
                    modelModifiers: { number: true },
                    type: "number",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), {
                label: "Kit expiration period (months)",
                "label-for": "kit-expiration",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormInput), {
                    id: "kit-expiration",
                    name: "kitExpiration",
                    modelValue: kitExpirationPeriod.value,
                    "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((kitExpirationPeriod).value = $event)),
                    type: "number",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), {
                label: "Kit manufactured date",
                "label-for": "kit-manufactured-date",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(BFormInput), {
                    id: "kit-manufactured-date",
                    name: "kitManufacturedDate",
                    modelValue: kitManufacturedDate.value,
                    "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((kitManufacturedDate).value = $event)),
                    type: "date"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _cache[28] || (_cache[28] = _createElementVNode("h5", {
                class: "mt-4",
                hidden: ""
              }, "License", -1)),
              _createElementVNode("h5", _hoisted_4, [
                _createVNode(_component_font_awesome_icon, {
                  icon: "ticket",
                  class: "me-2"
                }),
                _cache[24] || (_cache[24] = _createTextVNode(" Coupons ", -1))
              ]),
              _createVNode(_unref(BFormCheckbox), {
                id: "coupon-compute",
                name: "coupon-compute",
                modelValue: generateCouponStorage.value,
                "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((generateCouponStorage).value = $event)),
                class: "mt-3"
              }, {
                default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                  _createTextVNode(" Include coupon for storage ", -1)
                ]))]),
                _: 1
              }, 8, ["modelValue"]),
              _createVNode(_unref(BFormCheckbox), {
                id: "coupon-storage",
                name: "coupon-storage",
                modelValue: generateCouponCompute.value,
                "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((generateCouponCompute).value = $event)),
                class: "mt-3"
              }, {
                default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                  _createTextVNode(" Include coupon for compute ", -1)
                ]))]),
                _: 1
              }, 8, ["modelValue"]),
              _createVNode(_unref(BButton), {
                class: "mt-3",
                variant: "primary",
                type: "submit",
                disabled: mutationInProgress.value
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, {
                    icon: ['fas', 'ticket'],
                    class: "me-2"
                  }),
                  _cache[27] || (_cache[27] = _createElementVNode("span", null, " Generate Codes ", -1))
                ]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_unref(BAlert), {
                modelValue: mutationError.value,
                "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((mutationError).value = $event)),
                variant: "danger",
                dismissible: "",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(mutationError.value), 1)
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }),
    _createVNode(_unref(BModal), {
      modelValue: showNewCodesModal.value,
      "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((showNewCodesModal).value = $event)),
      onClose: _cache[18] || (_cache[18] = ($event: any) => (showNewCodesModal.value = false)),
      title: "New Codes Generated"
    }, {
      default: _withCtx(() => [
        _createElementVNode("p", null, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(newCodes.value, (newCode, idx) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: idx }, [
              _createElementVNode("span", null, _toDisplayString(newCode.code), 1),
              _cache[29] || (_cache[29] = _createElementVNode("br", null, null, -1))
            ], 64))
          }), 128))
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})